import './pique-action-plan-timeline';

import type { EditorHost } from '@blocksuite/block-std';
import { ShadowlessElement, WithDisposable } from '@blocksuite/block-std';
import { SignalWatcher } from '@lit-labs/preact-signals';
import type { FullSessionTranscriptionData } from '@pique/core/services/transcription-service';
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import {
  type ActionPlanStep,
  type ActionPlanTimelineEditDetail,
} from './pique-action-plan-timeline';
import type { PiqueDatabaseBlockComponent } from './pique-database-spec';

export type ActionPlanOption = ActionPlanStep;

export interface ActionPlanContext {
  taskTitle: string;
  taskDescription?: string;
  transcriptionData?: FullSessionTranscriptionData | null;
  pageContent?: string;
  rowId?: string;
  databaseComponent?: PiqueDatabaseBlockComponent;
}

@customElement('pique-action-plan-panel')
export class PiqueActionPlanPanel extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    .action-plan-container {
      position: relative;
      width: 100%;
      max-width: 483px;
      margin: 16px auto 0;
      box-sizing: border-box;
    }

    .action-plan-box {
      background: var(--affine-background-primary-color);
      border: 1px solid var(--affine-border-color);
      border-radius: 16px;
      padding: 16px 20px;
      min-height: auto;
      width: 100%;
      max-width: 483px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: none;
      box-sizing: border-box;
    }

    .action-plan-box.expanded {
      background: transparent;
      border: none;
      box-shadow: none;
      padding: 0;
      min-height: auto;
      flex-direction: column;
      align-items: stretch;
      gap: 0;
      width: 100%;
    }

    .action-plan-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
    }

    .action-plan-info {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .action-plan-title {
      font-size: var(--affine-font-sm);
      font-weight: 600;
      color: var(--affine-text-primary-color);
      margin: 0;
    }

    .action-plan-subtitle {
      font-size: var(--affine-font-xs);
      color: var(--affine-text-secondary-color);
      margin: 0;
    }

    .progress-info {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: var(--affine-text-secondary-color);
    }

    .progress-bar {
      width: 100px;
      height: 8px;
      background: var(--affine-background-tertiary-color);
      border-radius: 12px;
      overflow: hidden;
    }

    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, var(--affine-brand-color) 0%, rgba(139, 92, 246, 0.95) 50%, rgba(168, 85, 247, 0.9) 100%);
      border-radius: 12px;
      transition: width 0.3s ease;
      width: 0%;
    }

    .start-button {
      background: var(--pique-green-gradient-solid);
      color: #FFFFFF;
      border: none;
      border-radius: 20px;
      padding: 9px 16px 12px;
      font-size: var(--affine-font-xs);
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 140px;
      justify-content: center;
      box-shadow: var(--pique-green-shadow-medium);
    }

    .start-button:hover {
      background: var(--pique-green-gradient-solid);
      filter: brightness(1.1);
      transform: translateY(-1px);
      box-shadow: var(--pique-green-shadow-strong);
    }

    .start-button:active {
      transform: translateY(0);
    }

    .start-button.loading {
      cursor: not-allowed;
    }

    .loading-indicator {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 5px;
    }

    .loading-dot {
      width: 6px;
      height: 6px;
      background-color: rgba(255, 255, 255, 1);
      border-radius: 50%;
      animation: pulse 1.4s infinite ease-in-out both;
    }

    .loading-dot:nth-child(1) { animation-delay: -0.32s; }
    .loading-dot:nth-child(2) { animation-delay: -0.16s; }
    .loading-dot:nth-child(3) { animation-delay: 0s; }

    @keyframes pulse {
      0%, 80%, 100% { transform: scale(0); }
      40% { transform: scale(1.0); }
    }

  `;

  @property({ attribute: false })
  accessor options: ActionPlanOption[] = [];

  @property({ attribute: false })
  accessor host: EditorHost | undefined = undefined;

  @property({ attribute: false })
  accessor context: ActionPlanContext | undefined = undefined;

  @state()
  accessor isLoading = false;

  @state()
  accessor showOptions = false;

  override connectedCallback() {
    super.connectedCallback();
    // Attempt to load any pre-existing steps when the component mounts
    setTimeout(() => this.tryLoadExistingSteps(false), 0);
  }

  override updated(changed: Map<string, unknown>): void {
    super.updated?.(changed as any);
    if (changed.has('context') || changed.has('host')) {
      setTimeout(() => this.tryLoadExistingSteps(false), 0);
    }
  }

  private readonly handleStartClick = async () => {
    if (this.isLoading) return;

    this.isLoading = true;

    try {
      this.tryLoadExistingSteps(true);
      window.dispatchEvent(new CustomEvent('pique-action-plan-start'));
    } catch (error) {
      console.error('Error loading saved action plan steps:', error);
    } finally {
      this.isLoading = false;
    }
  };

  private readonly handleTimelineEdit = (
    event: CustomEvent<ActionPlanTimelineEditDetail>
  ) => {
    event.stopPropagation();
    this.updateOptionsFromEvent(event.detail.steps);
  };

  private updateOptionsFromEvent(steps: ActionPlanStep[]) {
    this.options = steps.map(step => ({
      ...step,
      id: String(step.id),
      completed: Boolean(step.completed),
    }));
    this.persistSteps(this.options);
  }

  /**
   * Fetch breakdown steps from the database model if available
   */
  private tryLoadExistingSteps(reveal: boolean): void {
    try {
      const rowId = this.context?.rowId;
      const db = this.context?.databaseComponent;
      if (!rowId || !db || typeof db.getBreakdownSteps !== 'function') {
        return;
      }

      const steps: ActionPlanOption[] = db.getBreakdownSteps(rowId) || [];
      if (!Array.isArray(steps) || steps.length === 0) {
        return;
      }

      this.options = steps.map(step => ({
        id: String(step.id),
        title: String(step.title ?? ''),
        description: String(step.description ?? ''),
        completed: Boolean((step as ActionPlanStep).completed),
      }));

      if (reveal) {
        this.showOptions = true;
      }
    } catch {
      // silently handle load failures
    }
  }

  private persistSteps(steps: ActionPlanOption[]): void {
    try {
      const rowId = this.context?.rowId;
      if (!rowId) return;

      const normalized = steps.map(opt => ({
        id: String(opt.id),
        title: String(opt.title ?? ''),
        description: String(opt.description ?? ''),
        completed: Boolean(opt.completed),
      }));

      const dbComponent = this.context?.databaseComponent;
      if (dbComponent?.handleBreakdownStepsUpdate) {
        dbComponent.handleBreakdownStepsUpdate(rowId, normalized);
        return;
      }

      const model = (dbComponent as { model?: { setBreakdownSteps?: (rowId: string, steps: typeof normalized) => void } } | undefined)?.model;
      model?.setBreakdownSteps?.(rowId, normalized);
    } catch {
      // silently handle persist failures
    }
  }

  override render() {
    const containerClass = classMap({
      'action-plan-box': true,
      'expanded': this.showOptions,
    });

    const buttonClass = classMap({
      'start-button': true,
      'loading': this.isLoading,
    });

    if (!this.showOptions) {
      return html`
        <div class="action-plan-container">
          <div class="${containerClass}">
            <div class="action-plan-content">
              <div class="action-plan-info">
                <h3 class="action-plan-title">
                  ${this.isLoading ? 'Creating steps...' : 'Ready to take the first step?'}
                </h3>
                ${!this.isLoading ? html`<p class="action-plan-subtitle">
                  Let's break down this task into smaller chunks.
                </p>` : ''}
              </div>
              <button class="${buttonClass}" @click="${this.handleStartClick}">
                ${this.isLoading
          ? html`<div class="loading-indicator">
                      <div class="loading-dot"></div>
                      <div class="loading-dot"></div>
                      <div class="loading-dot"></div>
                    </div>`
          : 'Start'
        }
              </button>
            </div>
          </div>
        </div>
      `;
    }

    return html`
      <div class="action-plan-container">
        <div class="${containerClass}">
          <pique-action-plan-timeline
            .steps=${this.options}
            @pique-timeline-step-edit=${this.handleTimelineEdit}
          ></pique-action-plan-timeline>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pique-action-plan-panel': PiqueActionPlanPanel;
  }
}
